import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Download, CheckCircle2, FileText, ChevronRight, MessageCircle, AlertTriangle } from "lucide-react";
import useCanonical from "../hooks/useCanonical";
import useJsonLd from "../hooks/useJsonLd";

const NOTICE_TYPES = [
  "GSTR-1 and GSTR-3B mismatch",
  "Missing GST return",
  "ITC claim denial",
  "Tax payment default",
  "Document verification request",
];

const buildTemplateText = (businessName) => `GST NOTICE RESPONSE TEMPLATE
Prepared by WINTRUST SOLUTIONS — Coimbatore
For: ${businessName}
https://wintrustsolutions.com

====================================================
[Date]

To,
Commissioner of GST
[Your GST Office Address]

Re: Response to GST Notice [Notice Reference Number]
Dated [Notice Date]

Dear Sir/Madam,

I acknowledge receipt of your notice dated [date] regarding
[brief reason: mismatch in returns / missing documents / ITC
issues / etc.].

I have reviewed the notice and hereby submit the following
response:

1. Facts: [Brief explanation of your position]

2. Documents attached: [List documents — invoices, bank
   statements, etc.]

3. Corrective action taken: [If applicable — filed amended
   return, paid outstanding amount, etc.]

I request your office to consider this response and close
the case. I am available for any clarification at
[phone number].

Respectfully,
[Your Name]
[GSTIN]
[Business Address]
[Signature]

Attachments: [List supporting documents]

====================================================
COMMON NOTICE TYPES THIS TEMPLATE COVERS
====================================================
- GSTR-1 and GSTR-3B mismatch
- Missing GST return
- ITC claim denial
- Tax payment default
- Document verification request

====================================================
NEED EXPERT HELP?
====================================================
WhatsApp : +91 89409 88776
Email    : wintrustsolutions@gmail.com
Service  : https://wintrustsolutions.com/gst-return-filing

DISCLAIMER: This is a template for reference only. Consult
a GST expert before submitting your response. Incorrect
responses may worsen your case.
`;

const downloadTxt = (filename, text) => {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const GSTNoticeTemplatePage = () => {
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useCanonical("/gst-notice-response-template");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "GST Notice Response Template — Free Download",
    "description": "Free GST notice response template for Coimbatore businesses. Reply within the deadline with a ready-to-use format.",
    "url": "https://wintrustsolutions.com/gst-notice-response-template",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!businessName.trim()) errs.businessName = "Business name is required";
    if (!phone.trim()) errs.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(phone.trim())) errs.phone = "Enter a valid 10-digit phone";
    if (!email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) errs.email = "Enter a valid email";

    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    downloadTxt(
      `GST-Notice-Response-Template-${businessName.trim().replace(/\s+/g, "-")}.txt`,
      buildTemplateText(businessName.trim())
    );

    const waUrl =
      `https://wa.me/918940988776?text=` +
      `Hi%20WinTrust%20Solutions%2C%0A%0A` +
      `I%20downloaded%20the%20GST%20Notice%20Response%20Template.%20My%20business%20is%20${encodeURIComponent(businessName.trim())}%20and%20phone%20is%20%2B91${encodeURIComponent(phone.trim())}.%20I%20have%20a%20GST%20notice%20and%20need%20help%20responding.`;
    window.open(waUrl, "_blank", "noopener,noreferrer");

    setSubmitted(true);
  };

  return (
    <>
      <Helmet>
        <title>GST Notice Response Template — Free Download for Coimbatore Businesses | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Free GST notice response template for Coimbatore businesses. Ready-to-use format to reply within deadline. Covers GSTR mismatch, ITC denial, tax default and more." />
      </Helmet>

      {/* Hero */}
      <section className="bg-navy py-12 md:py-16">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-4">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">GST Notice Template</span>
          </nav>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-heading leading-tight mb-4" data-testid="gst-notice-h1">
            Download Free GST Notice Response Template
          </h1>
          <p className="text-slate-300 text-lg max-w-3xl">
            Received a GST notice? Use this ready-to-use response template to reply within the deadline. No legal jargon — just fill your details and submit.
          </p>
        </div>
      </section>

      {/* Form + Template side-by-side */}
      <section className="bg-navy pb-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-10">
            {/* Download Form */}
            <div
              className="bg-white rounded-xl p-6 md:p-8 shadow-xl h-fit lg:sticky lg:top-28"
              data-testid="gst-notice-form-container"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 bg-gold/10 text-gold rounded-lg flex items-center justify-center">
                  <Download size={22} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-navy font-heading">Get the Template</h2>
                  <p className="text-slate-500 text-xs">Downloadable template + expert guidance</p>
                </div>
              </div>

              {submitted ? (
                <div
                  className="bg-green-50 border border-green-200 rounded-lg p-5 flex items-start gap-3"
                  data-testid="gst-notice-success"
                >
                  <CheckCircle2 className="text-green-600 flex-shrink-0 mt-0.5" size={22} />
                  <div>
                    <p className="text-navy font-semibold mb-1">Template downloaded. Opening WhatsApp for expert guidance...</p>
                    <p className="text-slate-600 text-sm">A GST specialist will respond within 2 hours.</p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" noValidate data-testid="gst-notice-form">
                  <div>
                    <label className="block text-navy text-sm font-medium mb-2" htmlFor="gst-business-name">
                      Business Name <span className="text-gold">*</span>
                    </label>
                    <input
                      id="gst-business-name"
                      type="text"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy focus:outline-none focus:border-gold"
                      placeholder="Your registered business name"
                      data-testid="gst-business-name-input"
                    />
                    {errors.businessName && <p className="text-red-600 text-xs mt-1" data-testid="gst-business-name-error">{errors.businessName}</p>}
                  </div>

                  <div>
                    <label className="block text-navy text-sm font-medium mb-2" htmlFor="gst-phone">
                      Phone Number <span className="text-gold">*</span>
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 bg-slate-100 border border-slate-200 border-r-0 rounded-l-lg text-slate-600 text-sm font-medium">+91</span>
                      <input
                        id="gst-phone"
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                        className="flex-1 bg-slate-50 border border-slate-200 rounded-r-lg px-4 py-3 text-navy focus:outline-none focus:border-gold"
                        placeholder="10-digit number"
                        data-testid="gst-phone-input"
                      />
                    </div>
                    {errors.phone && <p className="text-red-600 text-xs mt-1" data-testid="gst-phone-error">{errors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-navy text-sm font-medium mb-2" htmlFor="gst-email">
                      Email <span className="text-gold">*</span>
                    </label>
                    <input
                      id="gst-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-navy focus:outline-none focus:border-gold"
                      placeholder="you@example.com"
                      data-testid="gst-email-input"
                    />
                    {errors.email && <p className="text-red-600 text-xs mt-1" data-testid="gst-email-error">{errors.email}</p>}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gold hover:bg-gold/90 text-navy font-semibold py-3.5 rounded-lg flex items-center justify-center gap-2 transition-colors shadow-md"
                    data-testid="gst-notice-download-btn"
                  >
                    <Download size={18} />
                    Download Template
                  </button>

                  <p className="text-slate-500 text-xs text-center">
                    You'll get the template as a .txt file and be redirected to WhatsApp.
                  </p>
                </form>
              )}
            </div>

            {/* Template Preview */}
            <div className="text-white space-y-8" data-testid="gst-notice-preview">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-5">
                  <FileText className="text-gold" size={22} />
                  <h3 className="text-xl font-semibold font-heading">GST Notice Response Template</h3>
                </div>
                <div className="bg-navy/40 rounded-lg p-5 border border-white/5">
                  <pre className="text-slate-200 text-xs md:text-sm leading-relaxed whitespace-pre-wrap font-mono">
{`[Date]

To,
Commissioner of GST
[Your GST Office Address]

Re: Response to GST Notice [Notice Reference Number]
Dated [Notice Date]

Dear Sir/Madam,

I acknowledge receipt of your notice dated [date] regarding
[brief reason: mismatch in returns / missing documents / ITC
issues / etc.].

I have reviewed the notice and hereby submit the following
response:

1. Facts: [Brief explanation of your position]
2. Documents attached: [List documents — invoices, bank
   statements, etc.]
3. Corrective action taken: [If applicable — filed amended
   return, paid outstanding amount, etc.]

I request your office to consider this response and close
the case. I am available for any clarification at
[phone number].

Respectfully,
[Your Name]
[GSTIN]
[Business Address]
[Signature]

Attachments: [List supporting documents]`}
                  </pre>
                </div>
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-6 md:p-8 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-5">
                  <FileText className="text-gold" size={22} />
                  <h3 className="text-xl font-semibold font-heading">Common Notice Types Covered</h3>
                </div>
                <ul className="space-y-2.5">
                  {NOTICE_TYPES.map((t) => (
                    <li key={t} className="flex items-start gap-3">
                      <CheckCircle2 className="text-gold flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-slate-200 text-sm md:text-base">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Service link */}
              <div>
                <Link
                  to="/gst-return-filing"
                  className="text-gold font-semibold hover:underline underline-offset-4 inline-flex items-center gap-1"
                  data-testid="link-gst-filing"
                >
                  Need expert help with your GST notice? View our GST Compliance Services <ChevronRight size={16} />
                </Link>
              </div>
            </div>
          </div>

          {/* Urgent Amber CTA */}
          <div className="mt-12 bg-amber-100 border-l-4 border-amber-500 rounded-lg p-6 md:p-8 shadow-md" data-testid="urgent-amber-cta">
            <div className="flex flex-col md:flex-row md:items-center gap-5">
              <AlertTriangle className="text-amber-600 flex-shrink-0" size={36} />
              <div className="flex-1">
                <p className="text-navy font-semibold text-base md:text-lg mb-1">
                  Don't ignore this notice. Late response can result in ₹10,000+ penalties.
                </p>
                <p className="text-navy/80 text-sm">
                  WhatsApp us immediately for expert guidance.
                </p>
              </div>
              <a
                href="https://wa.me/918940988776?text=Hi%2C%20I%20received%20a%20GST%20notice%20and%20need%20urgent%20help"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-whatsapp flex-shrink-0 whitespace-nowrap"
                data-testid="urgent-whatsapp-btn"
              >
                <MessageCircle size={18} /> Get Help Now — WhatsApp Us
              </a>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="mt-8 text-slate-400 text-xs max-w-3xl">
            <strong className="text-slate-200">Disclaimer:</strong> This is a template for reference only. Consult a GST expert before submitting your response. Incorrect responses may worsen your case.
          </p>
        </div>
      </section>
    </>
  );
};

export default GSTNoticeTemplatePage;
